import { z } from 'zod';
import type { Prioridade } from './strategy';
import type { StudioToolKey } from './studio';

/* ─────────────────────────────────────────────────────────────────────
   Pulse — a estratégia em movimento.

   Toda semana o ADS grava uma linha por projeto: o retrato de como está
   agora — o que da estratégia foi construído, o que do plano foi feito,
   quantas evidências chegaram, o que o Studio entregou. A linha da semana
   corrente se atualiza até a semana virar; depois fica como está. A tela
   compara as linhas: o ritmo, o mês a mês e a distância do começo.
   Nada aqui chama a IA.
   ───────────────────────────────────────────────────────────────────── */

export const PULSE_TZ = 'America/Sao_Paulo';

const n = z.number().int().nonnegative().default(0);
export const pulseDataSchema = z.object({
  strategy: z.object({
    /** média do preenchimento das dez ferramentas, 0–100 */
    progress: z.number().int().min(0).max(100).default(0),
    /** ferramentas concluídas, de 10 */
    tools: n,
    /** o score do DNA ao vivo, 0–100 */
    score: z.number().int().min(0).max(100).default(0),
    /** maturidade média declarada no Mapa de Gaps, 0–5 (nulo: ainda não avaliada) */
    maturity: z.number().min(0).max(5).nullable().default(null),
    /** versões do DNA consolidadas */
    versions: n,
  }).prefault({}),
  actions: z.object({
    total: n, done: n, overdue: n,
    /** abertas com responsável / com prazo */
    owned: n, dated: n,
    invest: n, investDone: n,
  }).prefault({}),
  evidence: z.object({
    /** evidências anexadas às ferramentas (Mapa de Gaps, Diferenciais, 10 Tipos, Horizontes) */
    files: n,
    /** materiais lidos no Início */
    materials: n,
  }).prefault({}),
  studio: z.object({
    pieces: n, estrategia: n, presenca: n, narrativa: n,
    /** peças que foram ao ar (landing pages e sites) */
    published: n,
    tokens: n,
  }).prefault({}),
});
export type PulseData = z.infer<typeof pulseDataSchema>;
export const EMPTY_PULSE: PulseData = pulseDataSchema.parse({});

/** Uma semana: segunda-feira (AAAA-MM-DD), quando o retrato foi tirado e o retrato. */
export interface PulseWeek {
  week: string;
  at: string;
  data: PulseData;
  /** semana sem registro (o servidor esteve fora): repete a anterior */
  filled?: boolean;
}

export interface PulseActionDto { id: string; what: string; who: string; when: string; priority: Prioridade; doneAt: string }
export interface PulsePieceDto { id: string; title: string; tool: StudioToolKey; createdAt: string; published: boolean }

export interface PulseDto {
  /** hoje, no fuso da organização */
  today: string;
  /** a segunda-feira desta semana */
  week: string;
  /** da primeira semana registrada até esta, sem buracos */
  weeks: PulseWeek[];
  actions: {
    /** abertas com prazo daqui para a frente, as mais próximas primeiro */
    next: PulseActionDto[];
    overdue: PulseActionDto[];
    /** concluídas, as mais recentes primeiro */
    done: PulseActionDto[];
    /** abertas sem responsável */
    unowned: number;
    byPriority: Record<Prioridade, { total: number; done: number }>;
  };
  /** as peças mais recentes do Studio */
  pieces: PulsePieceDto[];
}

/* ── semanas ──────────────────────────────────────────────────────── */

const DAY = 86_400_000;
const utc = (d: string) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
export const addDays = (d: string, k: number) => iso(utc(d) + k * DAY);
/** A segunda-feira da semana de um dia. */
export const weekOf = (d: string) => addDays(d, -((new Date(utc(d)).getUTCDay() + 6) % 7));
export const daysBetween = (a: string, b: string) => Math.round((utc(b) - utc(a)) / DAY);

/** Ordena, tira repetidas e preenche as semanas sem registro com a anterior, até `until`. */
export function fillWeeks(rows: PulseWeek[], until: string): PulseWeek[] {
  const byWeek = new Map(rows.map(r => [r.week, r]));
  const sorted = [...byWeek.keys()].sort();
  if (!sorted.length) return [];
  const out: PulseWeek[] = [];
  const last = sorted.at(-1)! > until ? sorted.at(-1)! : until;
  for (let w = sorted[0]!; w <= last; w = addDays(w, 7)) {
    const r = byWeek.get(w);
    if (r) out.push(r);
    else out.push({ week: w, at: out.at(-1)!.at, data: out.at(-1)!.data, filled: true });
  }
  return out;
}

/* ── o que mudou ──────────────────────────────────────────────────── */

export interface PulseGains { progress: number; tools: number; score: number; actions: number; done: number; evidence: number; pieces: number; published: number }

export function pulseGains(a: PulseData, b: PulseData): PulseGains {
  return {
    progress: b.strategy.progress - a.strategy.progress,
    tools: b.strategy.tools - a.strategy.tools,
    score: b.strategy.score - a.strategy.score,
    actions: b.actions.total - a.actions.total,
    done: b.actions.done - a.actions.done,
    evidence: b.evidence.files - a.evidence.files,
    pieces: b.studio.pieces - a.studio.pieces,
    published: b.studio.published - a.studio.published,
  };
}

/**
 * O batimento de uma semana: quanto se avançou, somando as frentes com
 * pesos parecidos — 10 pontos de estratégia valem uma ação concluída
 * (que vale quatro ações planejadas), uma peça do Studio ou duas
 * evidências. Recuos não contam como avanço.
 */
export function weekBeat(g: PulseGains) {
  const p = (x: number) => Math.max(0, x);
  const parts = {
    estrategia: p(g.progress) / 10,
    plano: p(g.actions) * .25,
    execucao: p(g.done),
    evidencias: p(g.evidence) * .5,
    studio: p(g.pieces) + p(g.published) * .5,
  };
  return { ...parts, total: Object.values(parts).reduce((s, x) => s + x, 0) };
}

export interface PulseBeat { week: string; beat: number; gains: PulseGains; filled: boolean; first: boolean }
/** O batimento de cada semana, comparada com a anterior (a primeira é o ponto de partida). */
export function pulseBeats(weeks: PulseWeek[]): PulseBeat[] {
  return weeks.map((w, i) => {
    const gains = pulseGains(i ? weeks[i - 1]!.data : w.data, w.data);
    return { week: w.week, beat: i ? weekBeat(gains).total : 0, gains, filled: !!w.filled, first: i === 0 };
  });
}

/* ── o ritmo ──────────────────────────────────────────────────────── */

export type PulseRhythm = 'comecando' | 'acelerando' | 'constante' | 'desacelerando' | 'parado';
export const RHYTHM_LABEL: Record<PulseRhythm, string> = {
  comecando: 'Começando', acelerando: 'Acelerando', constante: 'Em ritmo constante', desacelerando: 'Desacelerando', parado: 'Parado',
};

/**
 * As últimas quatro semanas contra as quatro anteriores. Três semanas
 * seguidas sem avanço: parado. Menos de três semanas registradas: começando.
 */
export function pulseRhythm(weeks: PulseWeek[]) {
  const beats = pulseBeats(weeks);
  const recent = beats.slice(-4).filter(b => !b.first);
  const before = beats.slice(-8, -4).filter(b => !b.first);
  const sum = (xs: PulseBeat[]) => xs.reduce((s, b) => s + b.beat, 0);
  const active = recent.filter(b => b.beat > 0).length;
  let quiet = 0;
  for (let i = beats.length - 1; i > 0 && beats[i]!.beat === 0; i--) quiet++;
  let rhythm: PulseRhythm;
  if (beats.length < 3) rhythm = 'comecando';
  else if (quiet >= 3) rhythm = 'parado';
  else if (!before.length) rhythm = 'constante';
  else {
    const r = sum(recent) / Math.max(.5, sum(before));
    rhythm = r >= 1.25 ? 'acelerando' : r <= .6 ? 'desacelerando' : 'constante';
  }
  return { rhythm, active, of: recent.length, quiet, recent: sum(recent), before: sum(before) };
}

/* ── a fase da jornada ────────────────────────────────────────────── */

export type PulsePhase = 'estrategia' | 'plano' | 'pratica';
export const PHASES: ReadonlyArray<[PulsePhase, string, string]> = [
  ['estrategia', 'Construindo a estratégia', 'Diagnóstico, posicionamento e inovação no Strategy'],
  ['plano', 'Plano pronto', 'As ações definidas, com dono, prazo e custo'],
  ['pratica', 'Colocando em prática', 'Ações concluídas e entregas saindo do Studio'],
];
export const phaseOf = (d: PulseData): PulsePhase =>
  d.actions.done > 0 || d.studio.pieces > 0 ? 'pratica' : d.actions.total > 0 && d.strategy.progress >= 60 ? 'plano' : 'estrategia';

/** Quando cada fase começou: a primeira semana em que a fase (ou uma adiante) aparece. */
export function phaseStarts(weeks: PulseWeek[]): Record<PulsePhase, string | null> {
  const order = PHASES.map(([k]) => k);
  const out = { estrategia: null, plano: null, pratica: null } as Record<PulsePhase, string | null>;
  for (const w of weeks) {
    const at = order.indexOf(phaseOf(w.data));
    order.forEach((k, i) => { if (i <= at && !out[k]) out[k] = w.week; });
  }
  return out;
}

/* ── mês a mês ────────────────────────────────────────────────────── */

export interface PulseMonth { month: string; gains: PulseGains; end: PulseData; partial: boolean }
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
export const monthLabel = (m: string, year = false) => `${MONTHS[+m.slice(5, 7) - 1]}${year ? ` ${m.slice(0, 4)}` : ''}`;

/**
 * O mês de uma semana (AAAA-MM): o da quinta-feira, como no calendário ISO
 * — a semana fica no mês que tem a maior parte dela. A semana de hoje fica
 * no mês de hoje.
 */
export const weekMonth = (week: string, today: string) =>
  (week <= today && today <= addDays(week, 6) ? today : addDays(week, 3)).slice(0, 7);

/**
 * O crescimento de cada mês: o último retrato do mês contra o último do
 * mês anterior (o primeiro mês, contra a primeira semana registrada).
 */
export function pulseMonths(weeks: PulseWeek[], today: string): PulseMonth[] {
  if (!weeks.length) return [];
  const last = new Map<string, PulseData>();
  for (const w of weeks) last.set(weekMonth(w.week, today), w.data);
  let prev = weeks[0]!.data;
  return [...last.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, end]) => {
    const out = { month, gains: pulseGains(prev, end), end, partial: month === today.slice(0, 7) };
    prev = end;
    return out;
  });
}

/** Semanas desde a última vez que um número subiu (0: subiu nesta semana). */
export function weeksSince(weeks: PulseWeek[], pick: (d: PulseData) => number): number | null {
  for (let i = weeks.length - 1; i > 0; i--) if (pick(weeks[i]!.data) > pick(weeks[i - 1]!.data)) return weeks.length - 1 - i;
  return null;
}

/* ── o que pede atenção ───────────────────────────────────────────── */

export interface PulseSignal { key: string; tone: 'bom' | 'atencao' | 'alerta' | 'info'; text: string }

/** Os sinais, calculados dos números — sem IA. Primeiro o que pede ação, depois as boas notícias. */
export function pulseSignals(p: PulseDto): PulseSignal[] {
  const out: PulseSignal[] = [];
  const now = p.weeks.at(-1)?.data ?? EMPTY_PULSE;
  const month = pulseMonths(p.weeks, p.today).at(-1);
  const plural = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

  if (p.actions.overdue.length) {
    const first = p.actions.overdue[0]!;
    out.push({ key: 'atrasadas', tone: 'alerta', text: `${plural(p.actions.overdue.length, 'ação com prazo vencido', 'ações com prazo vencido')} — a mais antiga, "${first.what}", venceu há ${plural(daysBetween(first.when, p.today), 'dia', 'dias')}.` });
  }
  const soon = p.actions.next.find(a => daysBetween(p.today, a.when) <= 14);
  if (soon) {
    const d = daysBetween(p.today, soon.when);
    out.push({ key: 'proxima', tone: 'atencao', text: `"${soon.what}" vence ${d === 0 ? 'hoje' : d === 1 ? 'amanhã' : `em ${d} dias`}${soon.who ? ` — com ${soon.who}` : ', ainda sem responsável'}.` });
  }
  if (p.actions.unowned) out.push({ key: 'sem-dono', tone: 'atencao', text: `${plural(p.actions.unowned, 'ação aberta ainda sem responsável', 'ações abertas ainda sem responsável')}.` });
  if (!now.actions.total) out.push({ key: 'sem-plano', tone: 'info', text: 'O Plano de Ação ainda não tem ações: é por ele que o Pulse acompanha a execução.' });

  const stale = weeksSince(p.weeks, d => d.strategy.progress);
  if (now.strategy.progress < 100 && stale != null && stale >= 3) {
    out.push({ key: 'estrategia-parada', tone: 'info', text: `A estratégia não avança há ${stale} semanas — ${now.strategy.tools} de 10 ferramentas concluídas.` });
  }
  const studioQuiet = weeksSince(p.weeks, d => d.studio.pieces);
  if (!now.studio.pieces && now.actions.total) out.push({ key: 'studio-vazio', tone: 'info', text: 'Nenhuma peça no Studio ainda: o plano está pronto para virar entregas.' });
  else if (studioQuiet != null && studioQuiet >= 4) out.push({ key: 'studio-parado', tone: 'info', text: `Nenhuma peça nova no Studio há ${studioQuiet} semanas.` });

  if (month && month.gains.done > 0) out.push({ key: 'feitas', tone: 'bom', text: `${plural(month.gains.done, 'ação concluída', 'ações concluídas')} em ${monthLabel(month.month)}.` });
  if (month && month.gains.evidence > 0) out.push({ key: 'evidencias', tone: 'bom', text: `${plural(month.gains.evidence, 'evidência nova', 'evidências novas')} em ${monthLabel(month.month)}: o diagnóstico está sendo provado.` });
  if (month && month.gains.pieces > 0 && (studioQuiet == null || studioQuiet < 4)) out.push({ key: 'pecas', tone: 'bom', text: `${plural(month.gains.pieces, 'peça gerada', 'peças geradas')} no Studio em ${monthLabel(month.month)}.` });
  return out;
}
